"use client";

import * as React from "react";
import { useSearchParams } from "next/navigation";
import { LogIn } from "lucide-react";

function sanitizeNext(rawNext: string | null): string {
  if (!rawNext) return "/dashboard";

  let value = rawNext.trim();

  try {
    value = decodeURIComponent(value);
  } catch {}

  // Only allow internal absolute paths.
  if (!value.startsWith("/")) return "/dashboard";
  if (value.startsWith("//")) return "/dashboard";

  // Never redirect back to login or nested login loops.
  if (value === "/login") return "/dashboard";
  if (value.startsWith("/login?")) return "/dashboard";
  if (value.startsWith("/login/")) return "/dashboard";
  if (value.includes("next=%2Flogin") || value.includes("next=/login")) return "/dashboard";

  return value;
}

function LoginInner() {
  const sp = useSearchParams();

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setErr(null);

    try {
      // 1) Intentar super admin
      const adminRes = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "include",
        cache: "no-store",
      });

      const adminData = await adminRes.json().catch(() => null);

      if (adminRes.ok && adminData?.success) {
        window.location.replace(adminData.redirect || "/admin");
        return;
      }

      // Si admin responde algo distinto de 401 → mostrar error real
      if (adminRes.status !== 401) {
        setErr(adminData?.error || "Error en login de superadmin");
        return;
      }

      // 2) Intentar tenant solo si admin devolvió 401
      const tenantRes = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "include",
        cache: "no-store",
      });

      const tenantData = await tenantRes.json().catch(() => null);

      if (tenantRes.ok && tenantData?.ok) {
        // REQUIRED FIX 1 & 4: Bulletproof redirect and debug logs
        const target = sanitizeNext(sp.get("next") || tenantData.redirect || "/dashboard");

        console.info("[login] redirecting", {
          rawNext: sp.get("next"),
          apiRedirect: tenantData.redirect,
          target,
        });

        window.location.replace(target);
        return;
      }

      setErr(adminData?.error || tenantData?.error || "Credenciales inválidas");
    } catch {
      setErr("Error de conexión");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-dvh grid place-items-center p-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-2xl border p-5 space-y-4 bg-card/30 backdrop-blur"
        noValidate
      >
        <div className="text-center">
          <div className="text-xl font-semibold">Entrar</div>
          <div className="text-xs text-foreground/60">Zepelin — Panel</div>
        </div>

        <label className="grid gap-1">
          <span className="text-xs text-foreground/60">Email</span>
          <input
            type="email"
            className="rounded-xl border px-3 py-2"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
          />
        </label>

        <label className="grid gap-1">
          <span className="text-xs text-foreground/60">Contraseña</span>
          <input
            type="password"
            className="rounded-xl border px-3 py-2"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="current-password"
            required
          />
        </label>

        {err && (
          <div className="text-xs text-red-600" role="alert">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-muted disabled:opacity-60"
        >
          <LogIn className="h-4 w-4" />
          {loading ? "Entrando…" : "Entrar"}
        </button>

        <div className="text-[11px] text-foreground/60 text-center">
          Se establecerá una cookie segura según tu tipo de usuario.
        </div>
      </form>
    </div>
  );
}

export default function LoginPage() {
  return (
    <React.Suspense fallback={<div className="p-6 text-sm text-foreground/60">Cargando…</div>}>
      <LoginInner />
    </React.Suspense>
  );
}
